import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Input,
  Select,
  Space,
  Message,
  Empty,
  Popconfirm,
  Typography,
  Drawer,
  Slider,
  InputNumber,
  Tag,
} from '@arco-design/web-react'
import {
  IconPlus,
  IconDelete,
  IconSend,
  IconStop,
  IconSettings,
  IconMessage,
  IconEdit,
  IconRefresh,
} from '@arco-design/web-react/icon'
import { useQuery } from '@tanstack/react-query'
import { getUserModelsApi } from '@/api/user'
import { getApiKeysApi, getApiKeySecretApi } from '@/api/apiKeys'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import { streamChat, type StreamMessage } from '@/hooks/useStream'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { drawerWidth } from '@/utils/responsive'

interface ChatMessage extends StreamMessage {
  id: string
  createdAt: number
}

interface ChatSession {
  id: string
  title: string
  model: string
  systemPrompt: string
  messages: ChatMessage[]
  createdAt: number
  updatedAt: number
}

const STORAGE_KEY = 'chat_sessions_v1'
const ACTIVE_KEY = 'chat_sessions_active_v1'
const SETTINGS_KEY = 'chat_settings_v1'

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

function loadSessions(): ChatSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const list = JSON.parse(raw) as ChatSession[]
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

function saveSessions(list: ChatSession[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    // ignore quota errors
  }
}

interface LocalSettings {
  tokenId: number | null
  temperature: number
  maxTokens: number
  topP: number
  streamMode: boolean
}

function loadSettings(): LocalSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) return { ...defaults, ...JSON.parse(raw) }
  } catch {
    // ignore
  }
  return defaults
}

const defaults: LocalSettings = {
  tokenId: null,
  temperature: 0.7,
  maxTokens: 2048,
  topP: 1,
  streamMode: true,
}

export default function ChatPage() {
  const [sessions, setSessions] = useState<ChatSession[]>(() => loadSessions())
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(ACTIVE_KEY))
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [renameVisible, setRenameVisible] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [sessionsOpen, setSessionsOpen] = useState(false)
  const [settings, setSettings] = useState<LocalSettings>(() => loadSettings())
  const { isMobile } = useBreakpoint()

  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const modelsQuery = useQuery({ queryKey: ['user-models'], queryFn: () => getUserModelsApi() })
  const tokensQuery = useQuery({ queryKey: ['my-tokens'], queryFn: () => getApiKeysApi() })
  const { runSecure, verifyVisible, verifyScope, verifyContext, handleSuccess, handleCancel } =
    useSecureVerification()

  const tokenOptions = useMemo(() => {
    const items = (tokensQuery.data as { items?: Array<{ id: number; name: string }> } | undefined)?.items || []
    return items.map((t) => ({ label: t.name, value: t.id }))
  }, [tokensQuery.data])

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) || null,
    [sessions, activeId]
  )

  useEffect(() => {
    saveSessions(sessions)
  }, [sessions])

  useEffect(() => {
    if (activeId) localStorage.setItem(ACTIVE_KEY, activeId)
  }, [activeId])

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    } catch {
      // ignore
    }
  }, [settings])

  useEffect(() => {
    if (!settings.tokenId && tokenOptions.length > 0) {
      setSettings((s) => ({ ...s, tokenId: tokenOptions[0].value }))
    }
  }, [tokenOptions, settings.tokenId])

  useEffect(() => {
    if (sessions.length === 0) {
      createSession()
    } else if (!activeId || !sessions.some((s) => s.id === activeId)) {
      setActiveId(sessions[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    })
  }, [activeSession?.messages, activeSession?.id])

  const createSession = () => {
    const defaultModel = modelsQuery.data?.[0] || ''
    const sess: ChatSession = {
      id: newId(),
      title: '新会话',
      model: defaultModel,
      systemPrompt: '',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    setSessions((prev) => [sess, ...prev])
    setActiveId(sess.id)
  }

  const deleteSession = (id: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id)
      if (activeId === id) {
        setActiveId(next[0]?.id || null)
      }
      return next
    })
  }

  const clearMessages = () => {
    if (!activeSession) return
    setSessions((prev) =>
      prev.map((s) => (s.id === activeSession.id ? { ...s, messages: [], updatedAt: Date.now() } : s))
    )
  }

  const updateActive = (patch: Partial<ChatSession>) => {
    if (!activeSession) return
    setSessions((prev) =>
      prev.map((s) => (s.id === activeSession.id ? { ...s, ...patch, updatedAt: Date.now() } : s))
    )
  }

  const appendMessage = (msg: ChatMessage) => {
    if (!activeSession) return
    setSessions((prev) =>
      prev.map((s) =>
        s.id === activeSession.id ? { ...s, messages: [...s.messages, msg], updatedAt: Date.now() } : s
      )
    )
  }

  const patchMessage = (msgId: string, patch: Partial<ChatMessage>) => {
    if (!activeSession) return
    setSessions((prev) =>
      prev.map((s) =>
        s.id === activeSession.id
          ? {
              ...s,
              messages: s.messages.map((m) => (m.id === msgId ? { ...m, ...patch } : m)),
              updatedAt: Date.now(),
            }
          : s
      )
    )
  }

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || !activeSession) return
    if (!settings.tokenId) {
      Message.warning('请先在右上角设置中选择 API Key')
      setSettingsOpen(true)
      return
    }
    if (!activeSession.model) {
      Message.warning('请为当前会话选择模型')
      return
    }

    const userMsg: ChatMessage = { id: newId(), role: 'user', content: text, createdAt: Date.now() }
    const assistantMsg: ChatMessage = {
      id: newId(),
      role: 'assistant',
      content: '',
      createdAt: Date.now(),
    }

    const historyForApi: StreamMessage[] = []
    if (activeSession.systemPrompt.trim()) {
      historyForApi.push({ role: 'system', content: activeSession.systemPrompt.trim() })
    }
    activeSession.messages.forEach((m) =>
      historyForApi.push({ role: m.role, content: m.content })
    )
    historyForApi.push({ role: 'user', content: text })

    appendMessage(userMsg)
    appendMessage(assistantMsg)

    if (activeSession.title === '新会话' || !activeSession.title) {
      updateActive({ title: text.slice(0, 20) })
    }
    setInput('')
    setLoading(true)

    try {
      const keyResp = (await runSecure(
        (proof) => getApiKeySecretApi(settings.tokenId as number, proof),
        { scope: 'token.key.read' }
      )) as {
        key: string
      }
      const apiKey = `sk-${keyResp.key}`

      const controller = new AbortController()
      abortRef.current = controller

      await streamChat({
        apiKey,
        model: activeSession.model,
        messages: historyForApi,
        temperature: settings.temperature,
        max_tokens: settings.maxTokens,
        top_p: settings.topP,
        stream: settings.streamMode,
        signal: controller.signal,
        onDelta: (_, full) => patchMessage(assistantMsg.id, { content: full }),
        onDone: (full) => patchMessage(assistantMsg.id, { content: full }),
      })
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        Message.info('已中止生成')
      } else {
        const msg = err instanceof Error ? err.message : '请求失败'
        Message.error(msg)
        patchMessage(assistantMsg.id, { content: `[出错] ${msg}` })
      }
    } finally {
      setLoading(false)
      abortRef.current = null
    }
  }

  const stop = () => abortRef.current?.abort()

  const regenerate = async () => {
    if (!activeSession || activeSession.messages.length === 0) return
    const last = activeSession.messages[activeSession.messages.length - 1]
    if (last.role !== 'assistant') return
    const prevUser = [...activeSession.messages].reverse().find((m) => m.role === 'user')
    if (!prevUser) return
    const withoutLast = activeSession.messages.slice(0, -1)
    updateActive({ messages: withoutLast })
    setInput(prevUser.content)
    setTimeout(() => {
      setInput('')
      sendMessageFromText(prevUser.content, withoutLast)
    }, 0)
  }

  const sendMessageFromText = async (text: string, baseMsgs: ChatMessage[]) => {
    if (!activeSession || !settings.tokenId) return
    const assistantMsg: ChatMessage = {
      id: newId(),
      role: 'assistant',
      content: '',
      createdAt: Date.now(),
    }
    const historyForApi: StreamMessage[] = []
    if (activeSession.systemPrompt.trim())
      historyForApi.push({ role: 'system', content: activeSession.systemPrompt.trim() })
    baseMsgs.forEach((m) => historyForApi.push({ role: m.role, content: m.content }))
    if (baseMsgs[baseMsgs.length - 1]?.role !== 'user') {
      historyForApi.push({ role: 'user', content: text })
    }
    appendMessage(assistantMsg)
    setLoading(true)
    try {
      const keyResp = (await runSecure(
        (proof) => getApiKeySecretApi(settings.tokenId as number, proof),
        { scope: 'token.key.read' }
      )) as { key: string }
      const apiKey = `sk-${keyResp.key}`
      const controller = new AbortController()
      abortRef.current = controller
      await streamChat({
        apiKey,
        model: activeSession.model,
        messages: historyForApi,
        temperature: settings.temperature,
        max_tokens: settings.maxTokens,
        top_p: settings.topP,
        stream: settings.streamMode,
        signal: controller.signal,
        onDelta: (_, full) => patchMessage(assistantMsg.id, { content: full }),
        onDone: (full) => patchMessage(assistantMsg.id, { content: full }),
      })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '请求失败'
      Message.error(msg)
      patchMessage(assistantMsg.id, { content: `[出错] ${msg}` })
    } finally {
      setLoading(false)
      abortRef.current = null
    }
  }

  const renderSessionSidebar = (onSelect?: () => void) => (
    <>
      <div className="p-3 border-b">
        <Button
          long
          type="primary"
          icon={<IconPlus />}
          onClick={() => {
            createSession()
            onSelect?.()
          }}
        >
          新建会话
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-1">
        {sessions.length === 0 && <Empty description="暂无会话" className="py-6" />}
        {sessions.map((s) => (
          <div
            key={s.id}
            className={`group flex items-center gap-2 px-2 py-2 rounded cursor-pointer text-sm ${
              s.id === activeId ? 'bg-white shadow-sm' : 'hover:bg-white/60'
            }`}
            onClick={() => {
              setActiveId(s.id)
              onSelect?.()
            }}
          >
            <IconMessage className="text-gray-400 flex-shrink-0" />
            <div className="flex-1 truncate">{s.title || '未命名'}</div>
            <Button
              size="mini"
              type="text"
              icon={<IconEdit />}
              onClick={(e) => {
                e.stopPropagation()
                setRenameVisible(s.id)
                setRenameValue(s.title)
              }}
              className="!opacity-100 md:!opacity-0 md:group-hover:!opacity-100"
            />
            <Popconfirm
              title="确定删除该会话？"
              onOk={() => deleteSession(s.id)}
              triggerProps={{ onClick: (e) => e.stopPropagation() }}
            >
              <Button
                size="mini"
                type="text"
                status="danger"
                icon={<IconDelete />}
                onClick={(e) => e.stopPropagation()}
                className="!opacity-100 md:!opacity-0 md:group-hover:!opacity-100"
              />
            </Popconfirm>
          </div>
        ))}
      </div>
    </>
  )

  return (
    <div className="flex min-h-[min(calc(100vh-96px),720px)] md:h-[calc(100vh-96px)] gap-3 bg-white rounded-2xl overflow-hidden shadow">
      <aside className="hidden md:flex w-64 flex-shrink-0 border-r bg-[#F7F9FC] flex-col">
        {renderSessionSidebar()}
      </aside>

      <section className="flex-1 flex flex-col min-w-0">
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isMobile && (
              <Button size="small" icon={<IconMessage />} onClick={() => setSessionsOpen(true)}>
                会话
              </Button>
            )}
            <Typography.Text ellipsis className="font-medium">
              {activeSession?.title || '未命名会话'}
            </Typography.Text>
            <Select
              placeholder="模型"
              size="small"
              value={activeSession?.model}
              onChange={(v) => updateActive({ model: v })}
              options={(modelsQuery.data || []).map((m) => ({ label: m, value: m }))}
              showSearch
              className="w-full sm:w-[200px]"
              disabled={!activeSession}
            />
          </div>
          <Space wrap>
            <Button
              size="small"
              icon={<IconRefresh />}
              onClick={regenerate}
              disabled={!activeSession || loading}
            >
              重新生成
            </Button>
            <Button
              size="small"
              icon={<IconDelete />}
              onClick={clearMessages}
              disabled={!activeSession || activeSession.messages.length === 0}
            >
              清空消息
            </Button>
            <Button size="small" icon={<IconSettings />} onClick={() => setSettingsOpen(true)}>
              参数设置
            </Button>
          </Space>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-4 space-y-4 bg-[#FAFBFC]">
          {!activeSession || activeSession.messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <Empty description="从下方输入开始对话" />
            </div>
          ) : (
            activeSession.messages.map((m) => (
              <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                <div className="mb-1 text-xs text-gray-400">
                  {m.role === 'user' ? '我' : m.role === 'assistant' ? '助手' : '系统'}
                </div>
                <div
                  className={`inline-block whitespace-pre-wrap px-4 py-2 rounded-2xl max-w-[75%] text-left text-sm ${
                    m.role === 'user'
                      ? 'bg-[#005447] text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  {m.content || (loading && m.role === 'assistant' ? '…' : '')}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="sticky bottom-0 z-20 border-t p-3 space-y-2 bg-white/95 backdrop-blur">
          {activeSession && (
            <div className="flex items-center gap-2">
              <Typography.Text className="text-xs text-gray-400 flex-shrink-0">
                System Prompt
              </Typography.Text>
              <Input
                placeholder="可选：为当前会话设置系统角色"
                size="small"
                value={activeSession.systemPrompt}
                onChange={(v) => updateActive({ systemPrompt: v })}
              />
            </div>
          )}
          <div className="flex gap-2">
            <Input.TextArea
              placeholder="输入消息，Enter 发送，Shift+Enter 换行"
              value={input}
              onChange={setInput}
              autoSize={{ minRows: 2, maxRows: 6 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault()
                  if (!loading) sendMessage()
                }
              }}
              disabled={loading || !activeSession}
            />
            {loading ? (
              <Button status="warning" icon={<IconStop />} onClick={stop}>
                中止
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<IconSend />}
                onClick={sendMessage}
                disabled={!activeSession}
              >
                发送
              </Button>
            )}
          </div>
        </div>
      </section>

      <Drawer
        visible={sessionsOpen}
        title="会话列表"
        onCancel={() => setSessionsOpen(false)}
        footer={null}
        width={drawerWidth(280)}
        className="md:hidden"
        bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div className="flex flex-col h-full bg-[#F7F9FC]">{renderSessionSidebar(() => setSessionsOpen(false))}</div>
      </Drawer>

      {renameVisible && (
        <Drawer
          visible
          title="重命名会话"
          onCancel={() => setRenameVisible(null)}
          onOk={() => {
            if (renameVisible) {
              setSessions((prev) =>
                prev.map((s) =>
                  s.id === renameVisible ? { ...s, title: renameValue.trim() || s.title } : s
                )
              )
            }
            setRenameVisible(null)
          }}
          width={drawerWidth(320)}
          mask
        >
          <Input value={renameValue} onChange={setRenameValue} />
        </Drawer>
      )}

      <Drawer
        visible={settingsOpen}
        title="会话参数"
        onCancel={() => setSettingsOpen(false)}
        footer={null}
        width={drawerWidth(360)}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Text className="text-xs text-gray-500">API Key</Typography.Text>
            <Select
              placeholder="选择一个 API Key"
              value={settings.tokenId ?? undefined}
              onChange={(v) => setSettings((s) => ({ ...s, tokenId: v }))}
              options={tokenOptions}
              allowClear
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Typography.Text className="text-xs text-gray-500">Temperature</Typography.Text>
              <Tag>{settings.temperature.toFixed(2)}</Tag>
            </div>
            <Slider
              min={0}
              max={2}
              step={0.01}
              value={settings.temperature}
              onChange={(v) => setSettings((s) => ({ ...s, temperature: Number(v) }))}
            />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Typography.Text className="text-xs text-gray-500">Top P</Typography.Text>
              <Tag>{settings.topP.toFixed(2)}</Tag>
            </div>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={settings.topP}
              onChange={(v) => setSettings((s) => ({ ...s, topP: Number(v) }))}
            />
          </div>
          <div>
            <Typography.Text className="text-xs text-gray-500">Max Tokens</Typography.Text>
            <InputNumber
              value={settings.maxTokens}
              onChange={(v) => setSettings((s) => ({ ...s, maxTokens: v || 0 }))}
              min={1}
              max={32768}
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
          <div>
            <Typography.Text className="text-xs text-gray-500">流式</Typography.Text>
            <Select
              value={settings.streamMode ? 'stream' : 'sync'}
              onChange={(v) => setSettings((s) => ({ ...s, streamMode: v === 'stream' }))}
              options={[
                { value: 'stream', label: '流式返回（SSE）' },
                { value: 'sync', label: '同步返回' },
              ]}
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
        </Space>
      </Drawer>

      <SecureVerifyModal
        visible={verifyVisible}
        scope={verifyScope}
        context={verifyContext}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title="查看 API Key 需要二次校验"
      />
    </div>
  )
}
